import React, { useCallback, useEffect, useRef, useState } from "react";

import Module from "../../Module/Module";
import ModalBackdrop from "../../Modal/Backdrop";
import PopupModal from "../../Modal/Modal";
import SearchInput from "../../Input/SearchInput";

import axios from "axios";
import { LessonPlanAPIs } from "../../api";
import { CircularProgress } from "@material-ui/core";
import {
  getLinkPath,
  getSignedURL,
  removeFirstSlash,
  uploadFileToS3,
} from "../../../../utils/helper";
import { Close } from "@material-ui/icons";

const SAVE_DELAY = 4000; 

export function HeadCell({ col, syncColumn, clickRemove }) {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("");
  const saveRef = useRef(null);

  const saveColName = useCallback(
    async ({ newColName }) => {
      if (loading) return;
      setLoading(true);
      const updates = col.cellIds.map((id) => {
        return {
          cellIds: [id],
          data: { colName: newColName },
        };
      });
      try {
        const res = await axios.put(
          LessonPlanAPIs.updateCells(col.teacherId, col.lessonPlanId),
          {
            updates,
          },
          {
            headers: {
              token: window.localStorage.getItem("authToken"),
            },
          }
        );
        if (!res.data.data.updated) throw new Error();
        syncColumn(col, { colName: newColName });
      } catch (err) {
        console.log(err);
        alert("Something went wrong, while saving column name");
      }
      setLoading(false);
    },
    [loading, setLoading, value]
  );

  function onChange(value) {
    setValue(value);
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      saveColName({ newColName: value });
      clearTimeout(saveRef.current);
    }, SAVE_DELAY);
  }

  useEffect(() => {
    setValue(col.colName);
  }, [col.colName]);

  return (
    <div
      className="table-row-head"
      style={{
        display: "flex",
        alignItems: "start",
        cursor: loading ? "not-allowed" : "pointer",
        opacity: col.fixed ? 0.6 : 1,
      }}
    >
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={loading || col.fixed}
        style={{ cursor: loading || col.fixed ? "not-allowed" : "" }}
      ></textarea>
      {!col.fixed ? (
        <Close onClick={clickRemove} style={{ color: "white" }} />
      ) : null}
    </div>
  );
}

export function ModuleCell(props) {
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState({});
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(false);
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);

  async function updateCellModules() {
    try {
      if (progress) return;
      setProgress(true);
      await props.save({ colType: "resources", resources: selectedModules });
      setPopup({
        open: true,
        status: "Success",
        message: `Module Updated on row ${props.rowId}`,
      });
      closeModal();
    } catch (err) {
      setPopup({
        open: true,
        status: "Error",
        message: err.message || "Something Went Wrong!",
      });
    }
    setProgress(false);
  }

  function moduleSelector(id) {
    //unselect module
    const selected = selectedModules.filter((sm) => sm.id?._id !== id);
    if (selected.length !== selectedModules.length) {
      setSelectedModules(selected);
      return;
    }
    //select module
    const module = [...props.col.resources, ...modules].find(
      (m) => `${m.id?._id || ""}` === id
    );
    if (!module) return;
    selectedModules.push(module);
    setSelectedModules([...selectedModules]);
  }

  useEffect(() => {
    if (!props?.col?.resources || !props.col.resources.length) return;
    setSelectedModules([...props.col.resources]);
  }, [modules, props.col.resources]);

  const searchModule = useCallback(() => {
    if (!search) {
      setModules([]);
      return;
    }
    setSearching(true);
    axios
      .get(LessonPlanAPIs.searchModules(search), {
        headers: {
          token: localStorage.getItem("authToken"),
        },
        validateStatus: () => true,
      })
      .then((res) => {
        if (res.data.status !== "success") throw res.data;
        setModules(res.data.data.modules.splice(0, 30));
      })
      .catch((err) => {
        console.log(err.message);
        setModules([]);
      })
      .finally(() => {
        setSearching(false);
      });
  }, [searching, setSearching, setModules, search]);

  useEffect(() => {
    if (searching) return;
    const timeout = setTimeout(searchModule, 500);
    return () => clearTimeout(timeout);
  }, [search]);

  function closeModal() {
    setModules([]);
    setSelectedModules([]);
    setSearch("");
    setOpen(false);
  }

  return (
    <div className="text-cell module-cell">
      <div
        onClick={() => {
          setOpen(true);
        }}
        className="module-placeholder"
      >
        Click Here To Add Module
      </div>
      <br />
      {props?.col?.resources && props.col.resources.length ? (
        <div>
          {props.col.resources.map((resource, index) => (
            <Module resource={resource} key={index} />
          ))}
        </div>
      ) : (
        <></>
      )}

      <ModalBackdrop open={open}>
        <div className="module-modal-body">
          <SearchInput
            placeholder="Search Module Here"
            handleInput={(e) => setSearch(e.target.value)}
            value={search}
            readOnly={searching}
          />
          <p style={{ fontSize: "0.7rem", margin: "5px 0px" }}>
            Total Results <b>{modules.length}</b>
          </p>
          <div className="module-modal-contents">
            {modules.length ? (
              modules.map((resource, index) => {
                return (
                  <Module
                    resource={resource}
                    key={index}
                    checkbox={true}
                    editor={true}
                    selected={selectedModules.find(
                      (sM) => sM.id?._id === `${resource.id?._id}`
                    )}
                    selectModule={() => moduleSelector(resource.id?._id)}
                  />
                );
              })
            ) : searching ? (
              <div style={{ width: "fit-content", margin: "50px auto" }}>
                <CircularProgress />
              </div>
            ) : props?.col?.resources && props.col.resources.length ? (
              props.col.resources.map((resource, index) => {
                return (
                  <Module
                    resource={resource}
                    key={index}
                    checkbox={true}
                    editor={true}
                    selected={selectedModules.find(
                      (sM) => sM.id?._id === `${resource.id?._id}`
                    )}
                    selectModule={() => moduleSelector(resource.id?._id)}
                  />
                );
              })
            ) : (
              <div
                style={{ textAlign: "center", margin: "30px 0", color: "grey" }}
              >
                Search Modules
              </div>
            )}
          </div>
          <div className="buttons">
            <button onClick={closeModal}>Close</button>
            <button onClick={updateCellModules}>
              {progress ? "Adding..." : `Add (${selectedModules.length})`}
            </button>
          </div>
        </div>
      </ModalBackdrop>
      <PopupModal
        open={popup.open}
        heading={popup.status}
        message={"Module updated"}
      >
        <button
          style={{
            color: "#414141",
          }}
          onClick={() => setPopup({})}
        >
          Close
        </button>
      </PopupModal>
    </div>
  );
}

function TextCell({ col, save, status, setStatus }) {
  const saveRef = useRef(null);
  const [value, setValue] = useState("");

  function onChange(value) {
    setValue(value);
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      if (status === "updating") return;
      setStatus("updating");
      try {
        await save({ text: value });
        setStatus("updated");
      } catch (err) {
        setValue(col.text);
        setStatus("error");
      }
      const t = setTimeout(() => {
        setStatus("");
      }, 2000);
      clearInterval(t);
      clearTimeout(saveRef.current);
    }, SAVE_DELAY);
  }

  useEffect(() => {
    setValue(col.text);
  }, [col.text]);

  return (
    <div className={`text-cell`}>
      <textarea
        name={col._id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={col.fixed || status === "updating"}
        style={{ cursor: col.fixed ? "not-allowed" : "" }}
      ></textarea>
    </div>
  );
}

function LinkCell({ col, save, status, setStatus }) {
  const saveRef = useRef(null);
  const [value, setValue] = useState("");
  const [linkType, setLinkType] = useState("link");

  async function onFileUpload(file) {
    if (status === "updating") return;
    setStatus("updating");

    try {
      const { signedURL } = await getSignedURL({
        id: col._id,
        fileName: file.name,
        fileType: file.type,
      });
      await uploadFileToS3(file, signedURL);
      await save({
        file: {
          fileName: file.name,
          fileLink: signedURL.split("?")[0],
        },
      });
      setStatus("");
    } catch (err) {
      setStatus("error");
    }
  }

  async function removeFile() {
    if (status === "updating") return;
    setStatus("updating");
    try {
      await save({
        removeFile: true,
        fileLink: removeFirstSlash(getLinkPath(col.file?.fileLink)),
      });
      setStatus("");
    } catch (err) {
      setStatus("error");
    }
  }

  function onChange(value) {
    setValue(value);
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      if (status === "updating") return;
      setStatus("updating");
      try {
        await save({ text: value });
        setStatus("");
      } catch (err) {
        setValue(col.text);
        setStatus("error");
      }
      clearTimeout(saveRef.current);
    }, SAVE_DELAY);
  }

  useEffect(() => {
    setValue(col.text);
  }, [col.text]);

  return (
    <div className={`text-cell`}>
      {col.file ? (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <a target="_blank" rel="noreferrer" href={col.file?.fileLink}>
            {col.file?.fileName}
          </a>
          <Close
            style={{ color: "red", cursor: "pointer" }}
            onClick={removeFile}
          />
        </div>
      ) : (
        <>
          <select
            style={{ marginBottom: "10px", display: "block" }}
            onChange={(e) => setLinkType(e.target.value)}
            name="linkType"
          >
            <option value={"link"}>Link</option>
            <option value={"file"}>File</option>
          </select>
          {linkType === "link" ? (
            <textarea
              name={col._id}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              readOnly={col.fixed || status === "updating"}
              style={{ cursor: col.fixed ? "not-allowed" : "" }}
              placeholder="Paste link"
            ></textarea>
          ) : (
            <input
              name="file"
              type="file"
              onChange={(e) => onFileUpload(e.target.files[0])}
            />
          )}
        </>
      )}
      {status === "updating" ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        ></div>
      ) : null}
    </div>
  );
}

function Cell(props) {
  const [status, setStatus] = useState("");
  async function saveCellData(data) {
    const apiURL = props.col?._id
      ? LessonPlanAPIs.updateCell(
          props.col?.teacherId,
          props.col?.lessonPlanId,
          props.col?._id
        )
      : LessonPlanAPIs.addCell(props.col?.teacherId, props.col.lessonPlanId);
    const body = props.col?._id
      ? data
      : {
          colName: props.col.colName,
          colIndex: props.col.colIndex,
          type: props.col.colType,
          ...data,
        };

    const res = await axios.patch(apiURL, body, {
      headers: {
        "content-type": "application/json",
        token: localStorage.getItem("authToken"),
      },
    });
    if (res.data.status !== "success") throw res.data;
    if (data.removeFile) {
      delete data.removeFile;
      delete data.fileLink;
      data.file = undefined;
    }
    props.syncCell({ ...props.col, ...data }, res?.data?.data?._id || "");
  }

  return (
    <div
      class={`table-row-cell ${status}`}
      style={{
        opacity: props.col.fixed ? 0.8 : 1,
        cursor: props.col.fixed ? "not-allowed" : "",
      }}
    >
      {props.col.colType === "resources" ? (
        <ModuleCell key={`mod_${props.col._id}`} col={props.col} save={saveCellData} editor={true} />
      ) : null}
      {props.col.colType === "text" || !props.col.colType ? (
        <TextCell
          col={props.col}
          save={saveCellData}
          status={status}
          setStatus={setStatus}
        />
      ) : null}
      {props.col.colType === "link" ? (
        <LinkCell
          col={props.col}
          save={saveCellData}
          status={status}
          setStatus={setStatus}
        />
      ) : null}
    </div>
  );
}

export const MemoizedCell = React.memo(Cell);
